import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { RecordFactory } from '@common/data-models/records/record-factory';
import { ObjectProperties } from '@common/data-models/service/object-properties';
import { ServiceMoveRecord } from '@common/data-models/service/service-move-record';
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ObjectTypes } from '@common/enums/object-types';
import { Util } from '@common/utils/util';
import { DataStore } from '@src/suite/common/data-models/data-store/data-store';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { LinkObject } from '@src/suite/common/data-models/service/link-object';
import { ServiceCollectionRecord } from '@src/suite/common/data-models/service/service-collection-record';
import { SelectedTextReadRate } from '@src/suite/common/store/story/models/story.models';
import { initializeRecord } from '@src/suite/common/utils/record-utils';
import { AppService } from '@suite/app.service';

import { NomWebServicesJson } from './nom-web-services-json';

@Injectable({ providedIn: 'root' })
export class BasicContentService {
    private appService: AppService;
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        private ngZone: NgZone,
        injector: Injector
    ) {
        setTimeout(() => (this.appService = injector.get(AppService)));
    }

    public someText = 'someText';

    public async getBasicContent<T extends StorytellingRecord>(database: string, path: string, guid: string, hitHighlightTerm: string, contentType: string): Promise<ServiceResponse<T>> {
        // get
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get(`/api/${contentType}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                params: {
                    database,
                    path,
                    guid,
                    hitHighlightTerm,
                },
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to get basic content',
                detailMessage: `Unable to get basic content. \n\rStackTrace: NomWebServicesJson.getBasicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<T>(false, `Unable to get basic content ${error.toString()}`);
        }
        const rec: ServiceRecord = ServiceRecord.instance(response.body);
        const converted = this.nomWebServicesJson.convertor.fromServiceRecordV2<T>(rec);

        // await DataStore.set(converted);
        return new ServiceResponse<T>(true, converted);
    }

    public async deleteBasicContent(database: string, path: string, guid: string, type: ObjectTypes, record?: StorytellingRecord): Promise<ServiceResponse<RemoveRecordResponse>> {
        // delete
        const deleteBody = {
            database,
            path,
            guid,
            type,
            fieldsToUpdate: [],
        };
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.delete('/api/BasicContent', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                body: JSON.stringify(deleteBody),
                observe: 'response',
            });
        } catch (e) {
            const msg: string = 'Unable to delete content';
            let isLinkViolationError: boolean = false;
            if (e && e.error && e.error.indexOf('CAST_LINK_VIOLATION') !== -1) {
                isLinkViolationError = true;
            }
            if (!isLinkViolationError) {
                this.nomWebServicesJson.showErrorModal({
                    message: msg,
                    detailMessage: `Unable to delete content. \n\rStackTrace: NomWebServicesJson.deleteBasicContent(); \n\rError: ${e.error}`,
                });
            } else {
                e.error = `[CAST_LINK_VIOLATION] ${e.error}`;
            }
            return new ServiceResponse<RemoveRecordResponse>(false, `Unable to delete basic content ${e.error}`);
        }
        const rec: ServiceMoveRecord = Object.assign(new ServiceMoveRecord(), response.body);

        if (rec.NewPath && record) record.Path.fullPath = rec.NewPath;

        const updatedTopics: Map<string, StorytellingTopicRecord> = new Map();
        const updatedRundowns: Map<string, StorytellingRundownRecord> = new Map();
        const updatedStories: Map<string, StorytellingStoryRecord> = new Map();
        const updatedAssignments: Map<string, StorytellingAssignmentRecord> = new Map();

        if (rec.UpdatedRecords?.length > 0) {
            for (const updatedRecord of rec.UpdatedRecords) {
                if (updatedRecord?.ListData?.Type) {
                    switch (updatedRecord.ListData.Type) {
                        case ObjectTypes.TOPIC: {
                            const serviceCollectionRecord = ServiceCollectionRecord.instance(updatedRecord);
                            const converted = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceCollectionRecord);
                            for (const assignment of converted.children) {
                                updatedAssignments.set(assignment.Guid, assignment);
                            }
                            for (const story of converted.stories) {
                                updatedStories.set(story.Guid, story);
                            }
                            updatedTopics.set(converted.Guid, converted);
                            break;
                        }
                        case ObjectTypes.RUNDOWN: {
                            const serviceCollectionRecord = ServiceCollectionRecord.instance(updatedRecord);
                            const converted = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingRundownRecord>(serviceCollectionRecord);
                            for (const story of converted.children) {
                                updatedStories.set(story.Guid, story);
                            }
                            updatedRundowns.set(converted.Guid, converted);
                            break;
                        }
                        case ObjectTypes.SCRIPT: {
                            const serviceRecord = ServiceRecord.instance(updatedRecord);
                            const converted = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingRecord>(serviceRecord);
                            updatedStories.set(converted.Guid, converted);
                            break;
                        }
                        case ObjectTypes.ASSIGNMENT: {
                            const serviceRecord = ServiceRecord.instance(updatedRecord);
                            const converted = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingRecord>(serviceRecord);
                            updatedAssignments.set(converted.Guid, converted);
                            break;
                        }
                    }
                }
            }
        }

        return new ServiceResponse<RemoveRecordResponse>(true, {
            deletedRecord: record,
            updatedRecords: { topics: updatedTopics, assignments: updatedTopics, stories: updatedStories, rundowns: updatedRundowns },
        });
    }

    public async updateBasicContentOld<T extends ViewStateRecord>(
        database: string,
        path: string,
        guid: string,
        keepApproved: boolean,
        fields: { [id: string]: any },
        updateMessage: boolean,
        referal: string,
        vs: ViewStateRecord
    ): Promise<ServiceResponse<T>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        try {
            // loader.show();
            Util.escapeSpecialCharsForFileName(fields);
            const fieldsToUpdate = this.nomWebServicesJson.convertor.toENPSFields(fields);
            if (!updateMessage) {
                // always need to adjust mod time, let WS handle expires
                fieldsToUpdate.push({
                    FieldName: 'ModTime',
                    FieldValue: new Date().toJSON(),
                });
            }
            const type: number = fields.type || fields.Type;
            if (!type) {
                // throw error
                this.nomWebServicesJson.showErrorModal({
                    message: 'Unable to update content',
                    detailMessage: `Unable to update content. No type specified for object with path: ${path} and guid ${guid}`,
                });
                return new ServiceResponse<T>(false, 'Unable to update basic content: no type specified');
            }
            requestParams = {
                database,
                path,
                guid,
                type,
                keepApproved,
                fieldsToUpdate,
            };
            response = await this.nomWebServicesJson.patch('/api/BasicContent', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to update content',
                detailMessage: `Unable to update content. \n\rStackTrace: NomWebServicesJson.updateBasicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<T>(false, `Unable to update basic content ${error.toString()}`);
        }
        const objectProperties = new ObjectProperties(response.body);
        if (vs) {
            this.nomWebServicesJson.convertor.deserialize(vs, objectProperties);
            return new ServiceResponse<T>(true, <T>vs);
        }
    }

    public async updateBasicContentV2<T extends StorytellingRecord>(
        database: string,
        path: string,
        guid: string,
        keepApproved: boolean,
        fields: { [id: string]: any },
        updateMessage: boolean,
        record: T
    ): Promise<ServiceResponse<T>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        try {
            // loader.show();
            Util.escapeSpecialCharsForFileName(fields);
            const fieldsToUpdate = this.nomWebServicesJson.convertor.toENPSFields(fields);
            if (!updateMessage) {
                // always need to adjust mod time, let WS handle expires
                fieldsToUpdate.push({
                    FieldName: 'ModTime',
                    FieldValue: new Date().toJSON(),
                });
            }
            const type: number = fields.type || fields.Type;
            if (!type) {
                // throw error
                this.nomWebServicesJson.showErrorModal({
                    message: 'Unable to update content',
                    detailMessage: `Unable to update content. No type specified for object with path: ${path} and guid ${guid}`,
                });
                return new ServiceResponse<T>(false, 'Unable to update basic content: no type specified');
            }
            requestParams = {
                database,
                path,
                guid,
                type,
                keepApproved,
                fieldsToUpdate,
            };
            response = await this.nomWebServicesJson.patch('/api/BasicContent', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to update content',
                detailMessage: `Unable to update content. \n\rStackTrace: NomWebServicesJson.updateBasicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<T>(false, `Unable to update basic content ${error.toString()}`);
        }
        const objectProperties = new ObjectProperties(response.body);
        if (record) {
            this.nomWebServicesJson.convertor.deserializeV2(record, objectProperties);
            return new ServiceResponse<T>(true, record);
        }
        return new ServiceResponse<T>(true, null);
    }

    public async createBasicContent<T extends StorytellingRecord>(database: string, path: string, keepApproved: boolean, fields: { [id: string]: any }): Promise<ServiceResponse<T>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        try {
            Util.escapeSpecialCharsForFileName(fields);
            const fieldsToUpdate = this.nomWebServicesJson.convertor.toENPSFields(fields);
            // always need to include create time, let WS handle expires
            fieldsToUpdate.push({
                FieldName: 'CreateTime',
                FieldValue: new Date().toJSON(),
            });
            requestParams = {
                database,
                path,
                keepApproved,
                fieldsToUpdate,
            };
            response = await this.nomWebServicesJson.put('/api/BasicContent', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
            const objectProperties = new ObjectProperties(response.body);
            let record = null;
            if (fields.Type === ObjectTypes.PSTVIEWSTATE) {
                record = RecordFactory.createViewStateForType(fields.PSTViewStateType);
            } else {
                // Implement initialization and serialization for every object type separately
                switch (fields.Type) {
                    case ObjectTypes.SCRIPT: {
                        record = initializeRecord<StorytellingStoryRecord>(ObjectTypes.SCRIPT);
                        this.nomWebServicesJson.convertor.deserializeV2(record, objectProperties);
                        break;
                    }
                    case ObjectTypes.PUBLICATION_CHANNEL: {
                        record = initializeRecord<StorytellingPublicationRecord>(ObjectTypes.PUBLICATION_CHANNEL);
                        this.nomWebServicesJson.convertor.deserializeV2(record, objectProperties);
                        break;
                    }
                }
                // TODO: add other object types
            }

            if (!record) {
                this.nomWebServicesJson.showErrorModal({
                    message: 'Unable to create content',
                    detailMessage: `Unable to create content!. \n\r Missing implementenation for type: ${fields.Type}`,
                });
                return new ServiceResponse<T>(false, null);
            }
            return new ServiceResponse<T>(true, record);
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to create content',
                detailMessage: `Unable to create content!. \n\rStackTrace: NomWebServicesJson.createBasicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<T>(false, `Unable to create basic content ${error.toString()}`);
        }
    }

    public async createBasicContentOld<T extends ViewStateRecord>(database: string, path: string, keepApproved: boolean, fields: { [id: string]: any }, referal: string): Promise<ServiceResponse<T>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        try {
            Util.escapeSpecialCharsForFileName(fields);
            const fieldsToUpdate = this.nomWebServicesJson.convertor.toENPSFields(fields);
            // always need to include create time, let WS handle expires
            fieldsToUpdate.push({
                FieldName: 'CreateTime',
                FieldValue: new Date().toJSON(),
            });
            requestParams = {
                database,
                path,
                keepApproved,
                fieldsToUpdate,
            };
            response = await this.nomWebServicesJson.put('/api/BasicContent', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to create content',
                detailMessage: `Unable to create content!. \n\rStackTrace: NomWebServicesJson.createBasicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<T>(false, `Unable to create basic content ${error.toString()}`);
        }
        const objectProperties = new ObjectProperties(response.body);
        let record = null;
        if (fields.Type === ObjectTypes.PSTVIEWSTATE) {
            record = RecordFactory.createViewStateForType(fields.PSTViewStateType);
        } else {
            record = RecordFactory.createRecordForType(fields.Type);
        }

        this.nomWebServicesJson.convertor.deserialize(record, objectProperties);
        await DataStore.set(record);
        return new ServiceResponse<T>(true, DataStore.byGuid<T>(record.guid, referal));
    }

    public async getRecord<T extends StorytellingRecord>(database: string, path: string, guid: string): Promise<ServiceResponse<T>> {
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get('/api/BasicContent', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                params: {
                    database,
                    path,
                    guid,
                    hitHighlightTerm: '',
                    returnText: 'true',
                },
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load rundown content!',
                detailMessage: `Failed to load rundown content! \n\rStackTrace: NomWebServicesJson.getRecord(); \n\rError: ${error}`,
            });
            return new ServiceResponse<T>(false, null);
        }
        const serviceRecord = ServiceRecord.instance(response.body);
        const converted = this.nomWebServicesJson.convertor.fromServiceRecordV2<T>(serviceRecord);

        // await DataStore.set(converted);
        // return new ServiceResponse<T>(true, DataStore.byGuid<T>(converted.guid));
        return new ServiceResponse<T>(true, converted);
    }

    public async addLink<T extends boolean>(database: string, links: Array<LinkObject>): Promise<ServiceResponse<T>> {
        try {
            const response: HttpResponse<any> = await this.nomWebServicesJson.post(
                '/api/Link',
                {
                    ENPSDatabase: database,
                    Relations: links,
                },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                    }),
                    responseType: 'json',
                    observe: 'response',
                }
            );
            if (response.ok) {
                return new ServiceResponse<T>(true);
            }
            throw 'Failed';
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to create content',
                detailMessage: `Unable to create content!. \n\rStackTrace: NomWebServicesJson.addLink(); \n\rError: ${error}`,
            });
            return new ServiceResponse<T>(false, `Unable to create link ${error.toString()}`);
        }
    }

    public async createStoryTemplate(
        database: string,
        path: string,
        keepApproved: boolean,
        fields: { [id: string]: any },
        title: string,
        text: string
    ): Promise<ServiceResponse<StorytellingStoryRecord>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        try {
            if (fields) {
                Util.escapeSpecialCharsForFileName(fields);
            }
            const fieldsToUpdate = this.nomWebServicesJson.convertor.toENPSFields(fields);
            // always need to include create time, let WS handle expires
            fieldsToUpdate.push(
                {
                    FieldName: 'CreateTime',
                    FieldValue: new Date().toJSON(),
                },
                {
                    FieldName: 'Type',
                    FieldValue: ObjectTypes.SCRIPT,
                },
                {
                    FieldName: 'Title',
                    FieldValue: title,
                },
                {
                    FieldName: 'Text',
                    FieldValue: text,
                }
            );
            requestParams = {
                database,
                path,
                keepApproved,
                fieldsToUpdate,
            };
            response = await this.nomWebServicesJson.put('/api/BasicContent', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to story template',
                detailMessage: `Unable to story template!. \n\rStackTrace: NomWebServicesJson.createBasicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingStoryRecord>(false, `Unable to create story template ${error.toString()}`);
        }
        return new ServiceResponse<StorytellingStoryRecord>(true, response.body);
    }

    async selectedTextReadRateTime(text: string): Promise<ServiceResponse<SelectedTextReadRate>> {
        let response: HttpResponse<any> = null;

        try {
            const requestParams = {
                text,
            };
            response = await this.nomWebServicesJson.post('/api/TextTime', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Unable to story template',
                detailMessage: `Unable to story template!. \n\rStackTrace: NomWebServicesJson.createBasicContent(); \n\rError: ${error}`,
            });
            return new ServiceResponse<SelectedTextReadRate>(false, `Unable to create story template ${error.toString()}`);
        }
        return new ServiceResponse<SelectedTextReadRate>(true, response.body);
    }
}
